import { History } from "@shopify/app-bridge/actions"
import {
  ActionList,
  Banner,
  Button,
  Card,
  Checkbox,
  Icon,
  InlineGrid,
  Key,
  Layout,
  Page,
  Popover,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
} from "@shopify/polaris"
import type { ActionListItemDescriptor } from "@shopify/polaris/build/ts/src/types"
import { EmailIcon, OrdersStatusIcon, ViewIcon } from "@shopify/polaris-icons"
import { find, isEmpty } from "lodash-es"
import { Observer } from "mobx-react"
import qs from "qs"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { useIntercom } from "react-use-intercom"

import { postRouteMessage } from "@/admin-bridge/admin-bridge"
import { APICode } from "@/api/APICode"
import ordersApi from "@/api/orders"
import TP2 from "@/api/TP2"
import { ITrackingResultItem } from "@/api/TP2/tracking-info"
import { Container, hasPermission, TrackInfo } from "@/component"
import PermissionBadge from "@/component/PermissionBadge"
import TrackStatusProgressBar from "@/component/TrackStatusProgressBar/TrackStatusProgressBar"
import Permission from "@/enums/permission"
import { BetaFeature } from "@/models/IUserInfo"
import { NotificationHistoryModalMobx, TagsManageModalMobx } from "@/pages/Orders/components"
import { ShipmentInfo } from "@/pages/Orders/components/OrderInfo"
import { UpdateStatusSelectFrom } from "@/pages/Orders/UpdateStatus/types"
import { languageList, languageType } from "@/pages/Settings/EmailEdit/type"
import { getLanguagesList } from "@/pages/Settings/EmailEdit/utils"
import stores from "@/stores"
import AppBridge from "@/utils/appBridge"
import { canIUseBetaFeature } from "@/utils/BetaFeature"
import { maskOrderWordInT } from "@/utils/customerAuthGate"
import { TrackStatus } from "@/utils/TrackStatus"
import UrlUtils from "@/utils/UrlUtils"

import styles from "./Orders.module.scss"
import IStatus = TrackStatus.IStatus;

interface OrdersDetailProps {
  numberID?: string
  numberIDList?: string
  /** 抽屉模式下传入：返回按钮触发的关闭回调 */
  onClose?: () => void
}

interface ShipmentNavState {
  query?: string
  orders?: string
  orderIndex?: number | string
  orderName?: string
}

const OrdersDetail = ({ numberID, numberIDList, onClose }: OrdersDetailProps) => {
  const { t: rawT } = useTranslation("orders")
  // 白名单模式（超管后台 + 客户未授权）：文案里的 Order(s) 换成 ORD
  const t = maskOrderWordInT(rawT, stores.userInfoSore.isAdminScoped)
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigator = useNavigate()
  const { show: showIntercom } = useIntercom()

  // 优先从 location.state 读取（抽屉模式新入口），回退到 search 参数（兼容旧 /orders/detail 流）
  const navState = (location.state as ShipmentNavState | null) || null
  const ordersQuery = navState?.query ?? searchParams.get("query") ?? ""
  const [changeLoading, setChangeLoading] = useState(false)

  // URL 中的订单 id（如 ?id=8233613590595）。优先级高于 location.state / orderIndex 流。
  const urlOrderId = searchParams.get("id")

  const ordersListString = useMemo(() => {
    let ordersListString = navState?.orders || searchParams.get("orders")

    if (isEmpty(ordersListString)) {
      if (numberIDList) {
        ordersListString = numberIDList
      } else if (numberID) {
        ordersListString = numberID
      }
    }

    return ordersListString
  }, [navState?.orders, searchParams, numberIDList, numberID])

  const ordersList = useMemo(() => {
    return ordersListString ? ordersListString.split(",") : []
  }, [ordersListString])

  let __index = ordersList.findIndex((item) => item === numberID)
  const stateOrderIndex = navState?.orderIndex

  if (stateOrderIndex !== undefined && stateOrderIndex !== null && stateOrderIndex !== "") {
    __index = Number(stateOrderIndex)
  } else if (searchParams.has("orderIndex")) {
    __index = Number(searchParams.get("orderIndex") || "0")
  } else {
    if (__index === -1) {
      __index = 0
    }
  }

  // order index
  const [orderIndex, setOrderIndex] = useState(__index)

  const { userInfoSore, ordersStore } = stores


  const [data, setData] = useState<{
    expandTrackInfo: boolean
    track_link: string
    tracking?: ITrackingResultItem
  }>({ expandTrackInfo: false, track_link: "", tracking: undefined })

  // URL id 流：由 /v2/shipments/ids 换取的加密 Shipment ID 数组（用于分页切换）及其 v 标记
  const [shipmentIds, setShipmentIds] = useState<string[]>([])
  const [shipmentV, setShipmentV] = useState<number | undefined>(undefined)
  // fetch_empty=true：订单尚未同步到 ParcelWILL，无法追踪，展示提示 banner
  const [orderUnsynced, setOrderUnsynced] = useState(false)


  const onBack = () => {
    if (onClose) {
      onClose()
      return
    }
    navigator("/orders" + decodeURIComponent(typeof ordersQuery === "string" ? ordersQuery : ""))
  }

  const getData = useCallback((id: number, executiveTranslation: string, locale: languageType | string) => {
    if (ordersStore.orderInfoDataLoadedTime === 0){
      ordersStore.setOrderInfoData(undefined)
    }

    // 如果参数都没有，直接跳回Orders
    if (!ordersListString || !id) {
      navigator("/orders")
    }

    if (executiveTranslation === "false") {
      locale = userInfoSore.data.primary_locale || "en"
    }

    // console.log('OrderInfo.tsx', '如果和上一次不相同，就去执行获取数据', [this.props.id, prevProps.id])
    id && TP2.getTrackingInfo(id, locale, executiveTranslation)
      .then(({ data: { data, code, msg } }) => {
        if (APICode.OK !== code) {
          stores.ordersStore.setOrderInfoShow(false)
          return
        }

        stores.ordersStore.setOrderInfoData(data)
        stores.ordersStore.setOrderInfoDataLoadedTime(new Date().getTime())

        const tracking = find<ITrackingResultItem>(data?.tracking, item => parseInt(item.nid + "") === parseInt(id + ""))

        setData({ expandTrackInfo: false, track_link: data.track_link, tracking })

      })
      .catch(() => {
        // MARK: chuwen 2022/7/12 10:56 当出现错误就关闭弹窗
        stores.ordersStore.setOrderInfoShow(false)
      })
      .finally(() => {
        setChangeLoading(false)
      })
  }, [ordersListString, navigator, userInfoSore.data.primary_locale, setChangeLoading])

  /**
   * 用加密 Shipment ID 拉取 tracking-info，落库与回填逻辑与 getData 保持一致。
   * @param v /v2/shipments/ids 响应里的 v，原样透传给 tracking-info
   */
  const loadTrackingByShipmentId = useCallback((
    shipmentId: string,
    executiveTranslation: string,
    locale: languageType | string,
    v?: number,
  ) => {
    if (ordersStore.orderInfoDataLoadedTime === 0) {
      ordersStore.setOrderInfoData(undefined)
    }

    if (executiveTranslation === "false") {
      locale = userInfoSore.data.primary_locale || "en"
    }

    TP2.getTrackingInfo(shipmentId, locale, executiveTranslation, v)
      .then(({ data: { data: infoData, code } }) => {
        if (APICode.OK !== code) {
          stores.ordersStore.setOrderInfoShow(false)
          return
        }

        stores.ordersStore.setOrderInfoData(infoData)
        stores.ordersStore.setOrderInfoDataLoadedTime(new Date().getTime())

        const tracking = infoData?.tracking?.[0]

        setData({ expandTrackInfo: false, track_link: infoData.track_link, tracking })
      })
      .catch(() => {
        stores.ordersStore.setOrderInfoShow(false)
      })
      .finally(() => {
        setChangeLoading(false)
      })
  }, [ordersStore, userInfoSore.data.primary_locale])

  // URL id 流：用 order_id 换取加密 Shipment ID 数组（只拉一次，供分页切换）
  useEffect(() => {
    if (!urlOrderId) {
      return
    }

    let cancelled = false

    setChangeLoading(true)
    setOrderUnsynced(false)
    ordersApi.getShipmentIdsByOrderId(urlOrderId)
      .then(({ data: { code, data } }) => {
        if (cancelled) {
          return
        }

        // 订单尚未同步到 ParcelWILL：展示未同步提示 banner，不再拉取物流详情
        if (data?.fetch_empty) {
          setOrderUnsynced(true)
          setShipmentIds([])
          setChangeLoading(false)
          return
        }

        if (code !== APICode.OK || !data?.shipment_ids?.length) {
          stores.ordersStore.setOrderInfoShow(false)
          setShipmentIds([])
          setChangeLoading(false)
          return
        }

        setShipmentV(data.v)
        setShipmentIds(data.shipment_ids)
        setOrderIndex(0)
      })
      .catch(() => {
        if (!cancelled) {
          stores.ordersStore.setOrderInfoShow(false)
          setChangeLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [urlOrderId])

  const [executiveTranslation, setExecutiveTranslation] = useState("")

  const [selectTargetLanguage, setSelectTargetLanguage] = useState(userInfoSore.data.primary_locale || "en")
  const handleLanguageChange = useCallback((value: string) => {
    setExecutiveTranslation("true")
    setSelectTargetLanguage(value as languageType)
  }, [])

  let actuallyTranslationExecuted = data.tracking?.translation_executed === true

  if (executiveTranslation !== "") {
    actuallyTranslationExecuted = executiveTranslation === "true"
  }

  const toggleExecutiveTranslation = useCallback(function (value: boolean) {
    if (!value) {
      setExecutiveTranslation("false")
    } else {
      setExecutiveTranslation("true")
    }
  }, [])

  const [translateLanguageListActive, setTranslateLanguageListActive] = useState(false)
  const toggleTranslateLanguageList = useCallback(() => {
    setTranslateLanguageListActive(prevState => !prevState)
  }, [])

  // 最大index
  // URL id 流用 shipmentIds 分页，其它流用 ordersList
  const maxIndex = useMemo(
    () => (urlOrderId ? shipmentIds.length : ordersList.length) - 1,
    [urlOrderId, shipmentIds.length, ordersList.length],
  )

  // 缓存当前选择的语言显示文本
  const currentSelectLanguage = useMemo(() => {
    let language = languageList.find(item => item.value === selectTargetLanguage)?.name || "English"

    if (selectTargetLanguage === userInfoSore.data.primary_locale) {
      language += " (default)"
    }

    return language
  }, [selectTargetLanguage, userInfoSore.data.primary_locale])

  // 翻译物流信息
  const hasTranslatePermission = hasPermission(Permission.AutoTranslateTrackingDetails)
  const translateLanguageList: ActionListItemDescriptor[] = useMemo(() => {
    return getLanguagesList(userInfoSore.data.shop_locales, (userInfoSore.data.primary_locale || "en") as string)
      .map(item => ({
        content: item.isDefault ? `${item.name} (default)` : item.name,
        active: item.value === selectTargetLanguage,
        onAction: () => {
          if (hasTranslatePermission) {
            handleLanguageChange(item.value)
          }
        },
      }))
  }, [userInfoSore.data.shop_locales, currentSelectLanguage])

  useEffect(() => {
    // 优先级：URL 中的 id 高于 location.state / orderIndex 流
    // URL id 流：按 orderIndex 取对应加密 Shipment ID 加载（shipmentIds 就绪后触发）
    if (urlOrderId) {
      const shipmentId = shipmentIds[orderIndex]

      if (shipmentId) {
        loadTrackingByShipmentId(shipmentId, executiveTranslation, selectTargetLanguage, shipmentV)
      }

      return
    }

    if (canIUseBetaFeature(BetaFeature.NEW_SHIPMENT_INFO_PAGE)){
      const targetPath = `/orders/shipments/${ordersList[orderIndex]}`

      // 抽屉模式（onClose 存在）：用 React Router 同步路由 + 保留导航 state，让 useParams 感知切换。
      // 使用 push（默认）而非 replace，保证：抽屉里翻页 N 次 → 再跳新页面 → history.back() 能逐条
      // 回到先前的 shipment，而不是一步跳回 /orders。
      // state.orderIndex 同步成当前值，否则后退回到中间某个 shipment 时 index 会落在初始值。
      if (onClose && targetPath !== location.pathname) {
        const prevState = (location.state as ShipmentNavState | null) || null

        navigator(targetPath, {
          state: { ...prevState, orderIndex },
          preventScrollReset: true,
        })
      }

      if (AppBridge.exist()) {
        // 参考文档：https://shopify.dev/tools/app-bridge/actions/navigation/history
        const history = History.create(AppBridge.app)

        history.dispatch(History.Action.PUSH, targetPath)
      } else {
        postRouteMessage(targetPath)
      }
    }

    const orderId = Number(ordersList[orderIndex])

    if (!isNaN(orderId)) {
      getData(orderId, executiveTranslation, selectTargetLanguage)
    }
  }, [orderIndex, executiveTranslation, selectTargetLanguage, getData, loadTrackingByShipmentId, shipmentIds, shipmentV, ordersList, urlOrderId])

  // index切换时全局loading
  useEffect(() => {
    ordersStore.setOrderInfoDataLoadedTime(0)
  }, [orderIndex])

  // 局部loading
  useEffect(() => {
    setChangeLoading(true)
  }, [executiveTranslation, selectTargetLanguage])

  const getTransitDays = useCallback(() => {
    const transitDay = data.tracking?.transitDay

    return transitDay ? t("OrderInfo.nDay", { count: transitDay }) : "-"
  }, [data.tracking?.transitDay, t])

  // 计算标题文本
  const trackInfoTitle = useCallback(() => {
    const status = data.tracking?.status || "-"

    return `${status} · ${getTransitDays()}`
  }, [data.tracking?.status, getTransitDays])

  // 上一页
  const handlePgUp = () => {
    const newIndex = orderIndex - 1 < 0 ? 0 : orderIndex - 1

    setOrderIndex(newIndex)
    // 抽屉模式下用 state 维护 orderIndex，不污染 URL search；legacy /orders/detail 仍需 URL 持久化
    if (!onClose) {
      UrlUtils.setSearchByKey("orderIndex", String(newIndex))
    }
  }
  // 下一页
  const handlePgDown = () => {
    const newIndex = orderIndex + 1 > maxIndex ? maxIndex : orderIndex + 1

    setOrderIndex(newIndex)
    if (!onClose) {
      UrlUtils.setSearchByKey("orderIndex", String(newIndex))
    }
  }

  const navigate = useNavigate()

  // 缓存当前处理的订单ID
  // URL id 流没有 ordersList，用已加载 tracking 的 nid 作为当前 shipment 的数值 ID
  const currentNumberID = useMemo(
    () => (urlOrderId ? Number(data.tracking?.nid) : Number(ordersList[orderIndex])),
    [urlOrderId, data.tracking?.nid, ordersList, orderIndex],
  )

  return (
    <Observer>{() => {
      const { orderInfoData, orderInfoDataLoadedTime } = ordersStore
      // 未同步订单不再等待物流数据，避免头部一直骨架屏
      const dataLoading = orderInfoDataLoadedTime === 0 && !orderUnsynced
      const disabledTranslate = dataLoading || !hasTranslatePermission
      // 超管后台 + 客户未授权（白名单模式）：标题用运单号，More actions 和翻译控件都不展示
      const { isAdminScoped } = stores.userInfoSore
      // 还没有运单号时标题退回占位文案
      const trackingNumberTitle = data.tracking?.track_number || t("TrackingNumberNotAddedYet", "Not added yet")

      return (
        <Container id="PageOrdersDetail" className={"pb-5"}>
          <Page
            backAction={{ content: "Back", onAction: onBack }}
            title={!dataLoading && (isAdminScoped ? trackingNumberTitle : orderInfoData?.order)}
            titleMetadata={dataLoading ? <div className={"w-20"}><SkeletonDisplayText size="small" /></div> : null}
            secondaryActions={[
              {
                content: t("OrderInfo.ViewOnTrackingPage"),
                loading: dataLoading,
                icon: ViewIcon,
                disabled: orderUnsynced,
                onAction: () => window.open(orderInfoData?.track_link, "_blank"),
              },
            ]}
            /* eslint-disable-next-line */
            // @ts-ignore 三元包起来后 title 的 t() 联合类型不再被 prop 上下文收窄，沿用本块既有的忽略方式
            actionGroups={isAdminScoped ? undefined : [
              {
                // @ts-ignore
                title: t("email_edit:MoreActions"),
                disabled: dataLoading || orderUnsynced,
                actions: [
                  {
                    // @ts-ignore
                    content: t("orders:ViewNotificationHistory"),
                    prefix: <Icon source={EmailIcon} />,
                    accessibilityLabel: "Individual action label",
                    onAction: () => stores.ordersStore.loadMailSendRecords(currentNumberID),
                  },
                  {
                    // @ts-ignore
                    content: t("orders:ManuallyUpdateStatus"),
                    prefix: <Icon source={OrdersStatusIcon} />,
                    accessibilityLabel: "Individual action label",
                    onAction: () => {
                      const { search } = window.location

                      // 构建正则表达式，查找orderIndex的值
                      const regex = new RegExp("(orderIndex=)([^&]*)")

                      const searchUrl = search.replace(regex, "$1" + orderIndex)

                      const searchObj = {
                        from: UpdateStatusSelectFrom.MultiSelect,
                        query: encodeURIComponent(searchUrl),
                        number_ids: [currentNumberID], // 当没有选择更多时，会有此参数
                      }

                      navigate({
                        pathname: "/orders/update-status",
                        search: qs.stringify(searchObj),
                      })
                    },
                  },
                ],
              },
            ]}
            pagination={{
              hasPrevious: dataLoading ? false : orderIndex > 0,
              hasNext: dataLoading ? false : orderIndex < maxIndex,

              onNext: handlePgDown,
              onPrevious: handlePgUp,

              previousKeys: [Key.KeyJ],

              // @ts-ignore
              previousTooltip: t("common:Pagination.previous") + " (J)",

              nextKeys: [Key.KeyK],

              // @ts-ignore
              nextTooltip: t("common:Pagination.next") + " (K)",
            }}
          >
            <div className={styles.sectionContainer}>
              {orderUnsynced &&
                <div className={"mb-4"}>
                  <Banner
                    tone="warning"
                    title={t("UnsyncedOrderTitle")}
                    action={{ content: t("UnsyncedOrderContactSupport"), onAction: () => showIntercom() }}
                  >
                    <p>{t("UnsyncedOrderDescription", { parcelPanel: "CWILL Order Tracking" })}</p>
                  </Banner>
                </div>
              }
              <Layout>
                <Layout.Section>
                  <Card>
                    <InlineGrid columns={{ xs: "auto", sm: "auto", md: "1fr auto", lg: "1fr auto", xl: "1fr auto" }}>
                      <Text as="h2" variant="headingSm">
                        {t("TrackingDetails")}
                      </Text>

                      {isAdminScoped ? <div /> : <div className={"flex gap-2 items-center flex-wrap"}>
                        <Checkbox
                          label={t("Translate")}
                          disabled={disabledTranslate || changeLoading || orderUnsynced}
                          checked={actuallyTranslationExecuted}
                          onChange={toggleExecutiveTranslation}
                        />
                        <div className={"flex items-center flex-wrap"}>
                          <Popover
                            active={translateLanguageListActive}
                            activator={<Button disabled={dataLoading || changeLoading || orderUnsynced}
                              disclosure
                              onClick={toggleTranslateLanguageList}
                                       >{currentSelectLanguage}</Button>}
                            autofocusTarget={"first-node"}
                            onClose={toggleTranslateLanguageList}
                          >
                            <ActionList
                              actionRole={"menuitem"}
                              items={translateLanguageList}
                              onActionAnyItem={toggleTranslateLanguageList}
                            />
                          </Popover>

                          {!hasPermission(Permission.AutoTranslateTrackingDetails) && <PermissionBadge
                            text={"Professional"}
                            level={3}
                            permission={Permission.AutoTranslateTrackingDetails}
                                                                                      />}
                        </div>
                      </div>}
                    </InlineGrid>

                    <div className={"flex flex-col gap-[var(--p-space-300)] mt-4"}>
                      {
                        dataLoading || changeLoading
                          ? <>
                            <SkeletonBodyText lines={1} />

                            <SkeletonBodyText lines={14} />
                          </>
                          : <>
                            <Text variant="headingMd" as="h2">{trackInfoTitle()}</Text>

                            {/* 订单状态进度指示器 */}
                            <TrackStatusProgressBar orderStatus={(data.tracking?.stausDataNum ?? 1) as IStatus} />

                            {data.tracking?.pickup_location && <div>
                              <Text as="span">{data.tracking?.pickup_location} </Text>
                            </div>}
                            {/* 物流信息 */}
                            {
                              !data.tracking?.trackinfo.length
                                ? (<p>{t("OrderInfo.NotYet")}</p>)
                                : <TrackInfo tracking={data.tracking} />
                            }
                          </>
                      }
                    </div>
                  </Card>

                </Layout.Section>
                <Layout.Section variant="oneThird">
                  <ShipmentInfo changeLoading={changeLoading} tracking={data.tracking} />
                </Layout.Section>
              </Layout>
            </div>
            <TagsManageModalMobx type={"detail"} />

            <NotificationHistoryModalMobx />
          </Page>
        </Container>
      )
    }}</Observer>
  )
}

export default OrdersDetail
